import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, Validators, FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { PasswordModule } from 'primeng/password';
import { Router, RouterLink } from '@angular/router';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SSOService } from '../../services/sso.service';
import { NotificationService } from '../../services/notification.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    FloatLabelModule,
    CardModule,
    CheckboxModule,
    PasswordModule,
    RouterLink
  ],
  templateUrl: './login.html',
  styleUrls: ['./login.css']
})
export class Login implements OnInit, OnDestroy {
  rememberMe: boolean = false;
  isLoading: boolean = false;
  ssoLoading: boolean = false;
  private authSubscription?: Subscription;

  constructor(
    private router: Router,
    private ssoService: SSOService,
    private notificationService: NotificationService
  ) {

  }

  ngOnInit(): void {
    // Wait for SSO service initialization before checking state
    this.initializeSSOCheck();

    // Subscribe to authentication state changes
    this.authSubscription = this.ssoService.isAuthenticated$.subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        console.log('User authenticated via SSO, navigating to home...');
        this.router.navigate(['/home']);
      }
    });
  }

  private async initializeSSOCheck(): Promise<void> {
    try {
      // Wait for SSO service to be ready and check authentication
      const isInitialized = await this.ssoService.waitForInitialization();
      if (isInitialized) {
        console.log('SSO initialized and user authenticated, redirecting to home');
        this.router.navigate(['/home']);
      } else {
        console.log('SSO initialized but user not authenticated, staying on login page');
      }
    } catch (error) {
      console.error('SSO initialization failed:', error);
    }
  }

  ngOnDestroy(): void {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }

  async onSSOLogin(): Promise<void> {
    this.ssoLoading = true;
    
    try {
      // Use popup method for better UX (redirect can be used as fallback)
      await this.ssoService.loginWithPopup();
      this.notificationService.success(
        'Authentication Successful', 
        'You have been successfully authenticated with Microsoft SSO.'
      );
    } catch (error) {
      console.error('SSO Login failed:', error);
      
      // Check if it's a popup blocked error
      if (error instanceof Error && error.message.includes('popup')) {
        // Fallback to redirect method if popup fails
        try {
          console.log('Popup blocked, falling back to redirect method...');
          this.notificationService.info(
            'Redirecting...', 
            'Popup was blocked. Redirecting to Microsoft sign-in page...'
          );
          await this.ssoService.loginWithRedirect();
        } catch (redirectError) {
          console.error('SSO Redirect login also failed:', redirectError);
          this.notificationService.error(
            'Authentication Failed', 
            'Unable to authenticate with Microsoft SSO. Please try again or contact support.'
          );
        }
      } else {
        this.notificationService.error(
          'Authentication Failed', 
          'Unable to authenticate with Microsoft SSO. Please check your network connection and try again.'
        );
      }
    } finally {
      this.ssoLoading = false;
    }
  }

}